import React, { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { lumi } from "../lib/lumi"
import toast from "react-hot-toast"
import {Mail, CheckCircle, Clock, Trash2, ArrowLeft, Search, Filter, Eye, MessageSquare} from 'lucide-react'

interface Contact {
  _id: string
  fullName: string
  email: string
  country: string
  message: string
  handled: boolean
  createdAt: string
}

export default function AdminContactInbox() {
  const navigate = useNavigate()
  const [contacts, setContacts] = useState<Contact[]>([])
  const [filteredContacts, setFilteredContacts] = useState<Contact[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState("")
  const [statusFilter, setStatusFilter] = useState<"all" | "unhandled" | "handled">("all")
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null)
  const [showDetailModal, setShowDetailModal] = useState(false)

  useEffect(() => {
    fetchContacts()
  }, [])

  useEffect(() => {
    filterContacts()
  }, [contacts, searchQuery, statusFilter])

  const fetchContacts = async () => {
    setIsLoading(true)
    try {
      const { list } = await lumi.entities.Contacts.list({
        sort: { createdAt: -1 },
        limit: 200
      })
      setContacts(list)
      console.log("📬 Fetched contacts:", list.length)
    } catch (error) {
      console.error("Failed to fetch contacts:", error)
      toast.error("Failed to load contact submissions")
    } finally {
      setIsLoading(false)
    }
  }

  const filterContacts = () => {
    let filtered = [...contacts]

    // Status filter
    if (statusFilter === "handled") {
      filtered = filtered.filter(c => c.handled)
    } else if (statusFilter === "unhandled") {
      filtered = filtered.filter(c => !c.handled)
    }

    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase()
      filtered = filtered.filter(c =>
        c.fullName.toLowerCase().includes(query) ||
        c.email.toLowerCase().includes(query) ||
        c.country.toLowerCase().includes(query) ||
        c.message.toLowerCase().includes(query)
      )
    }

    setFilteredContacts(filtered)
  }

  const toggleHandled = async (contact: Contact) => {
    try {
      await lumi.entities.Contacts.update(contact._id, {
        handled: !contact.handled
      })
      
      setContacts(prev => prev.map(c => 
        c._id === contact._id ? { ...c, handled: !c.handled } : c
      ))
      
      toast.success(contact.handled ? "Marked as unhandled" : "Marked as handled")
    } catch (error) {
      console.error("Failed to update contact:", error)
      toast.error("Failed to update status")
    }
  }

  const deleteContact = async (contactId: string) => {
    if (!window.confirm("Delete this contact submission? This cannot be undone.")) return

    try {
      await lumi.entities.Contacts.delete(contactId)
      setContacts(prev => prev.filter(c => c._id !== contactId))
      toast.success("Contact deleted")
      setShowDetailModal(false)
      setSelectedContact(null)
    } catch (error) {
      console.error("Failed to delete contact:", error)
      toast.error("Failed to delete contact")
    }
  }

  const viewDetails = (contact: Contact) => {
    setSelectedContact(contact)
    setShowDetailModal(true)
  }

  const getUnhandledCount = () => contacts.filter(c => !c.handled).length

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center gap-4">
          <button
            onClick={() => navigate("/dashboard/admin")}
            className="flex items-center gap-2 text-gray-600 hover:text-teal-600 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="font-medium">Back to Dashboard</span>
          </button>
          <div className="flex-1" />
          <h1 className="text-xl font-bold text-gray-900">Contact Inbox</h1>
          {getUnhandledCount() > 0 && (
            <span className="bg-red-500 text-white text-sm font-bold px-3 py-1 rounded-full">
              {getUnhandledCount()} unhandled
            </span>
          )}
        </div>
      </header>

      <div className="max-w-7xl mx-auto p-6">
        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by name, email, country, or message..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Status Filter */}
            <div className="flex items-center gap-2">
              <Filter className="w-5 h-5 text-gray-400" />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as any)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              >
                <option value="all">All Contacts</option>
                <option value="unhandled">Unhandled Only</option>
                <option value="handled">Handled Only</option>
              </select>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center gap-3">
              <Mail className="w-8 h-8 text-gray-400" />
              <div>
                <p className="text-2xl font-bold text-gray-900">{contacts.length}</p>
                <p className="text-sm text-gray-600">Total Contacts</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center gap-3">
              <Clock className="w-8 h-8 text-orange-500" />
              <div>
                <p className="text-2xl font-bold text-gray-900">{getUnhandledCount()}</p>
                <p className="text-sm text-gray-600">Unhandled</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center gap-3">
              <CheckCircle className="w-8 h-8 text-teal-600" />
              <div>
                <p className="text-2xl font-bold text-gray-900">{contacts.filter(c => c.handled).length}</p>
                <p className="text-sm text-gray-600">Handled</p>
              </div>
            </div>
          </div>
        </div>

        {/* Contacts Table */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
            </div>
          ) : filteredContacts.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-gray-500">
              <Mail className="w-12 h-12 mb-2 text-gray-300" />
              <p className="text-lg font-medium">No contacts found</p>
              <p className="text-sm">
                {searchQuery || statusFilter !== "all" 
                  ? "Try adjusting your filters" 
                  : "Contact submissions will appear here"}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Email
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Country
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Message Preview
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredContacts.map((contact) => (
                    <tr key={contact._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        {contact.handled ? (
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-teal-100 text-teal-800 text-xs font-semibold rounded-full">
                            <CheckCircle className="w-3 h-3" />
                            Handled
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-orange-100 text-orange-800 text-xs font-semibold rounded-full">
                            <Clock className="w-3 h-3" />
                            Unhandled
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{contact.fullName}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-600">{contact.email}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-600">{contact.country}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-600 max-w-md truncate">
                          {contact.message}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-500">
                          {new Date(contact.createdAt).toLocaleDateString()}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => viewDetails(contact)}
                            className="text-teal-600 hover:text-teal-800 transition-colors"
                            title="View details"
                          >
                            <Eye className="w-5 h-5" />
                          </button>
                          <button
                            onClick={() => toggleHandled(contact)}
                            className="text-gray-600 hover:text-gray-800 transition-colors"
                            title={contact.handled ? "Mark as unhandled" : "Mark as handled"}
                          >
                            {contact.handled ? (
                              <Clock className="w-5 h-5" />
                            ) : (
                              <CheckCircle className="w-5 h-5" />
                            )}
                          </button>
                          <button
                            onClick={() => deleteContact(contact._id)}
                            className="text-red-600 hover:text-red-800 transition-colors"
                            title="Delete"
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Detail Modal */}
      {showDetailModal && selectedContact && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900">Contact Details</h2>
              <button
                onClick={() => setShowDetailModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <span className="text-2xl">×</span>
              </button>
            </div>
            <div className="p-6 space-y-4">
              {/* Status Badge */}
              <div>
                {selectedContact.handled ? (
                  <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-teal-100 text-teal-800 text-sm font-semibold rounded-full">
                    <CheckCircle className="w-4 h-4" />
                    Handled
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-orange-100 text-orange-800 text-sm font-semibold rounded-full">
                    <Clock className="w-4 h-4" />
                    Unhandled
                  </span>
                )}
              </div>

              {/* Contact Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">Full Name</label>
                  <p className="text-gray-900 font-medium">{selectedContact.fullName}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">Country</label>
                  <p className="text-gray-900 font-medium">{selectedContact.country}</p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">Email</label>
                <a 
                  href={`mailto:${selectedContact.email}`}
                  className="text-teal-600 hover:text-teal-700 font-medium"
                >
                  {selectedContact.email}
                </a>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">Submitted On</label>
                <p className="text-gray-900">{new Date(selectedContact.createdAt).toLocaleString()}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">Message</label>
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <p className="text-gray-900 whitespace-pre-wrap">{selectedContact.message}</p>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="p-6 border-t border-gray-200 flex justify-between">
              <button
                onClick={() => toggleHandled(selectedContact)}
                className={`px-6 py-2 rounded-lg transition-colors flex items-center gap-2 ${
                  selectedContact.handled
                    ? "bg-orange-600 text-white hover:bg-orange-700"
                    : "bg-teal-600 text-white hover:bg-teal-700"
                }`}
              >
                {selectedContact.handled ? (
                  <>
                    <Clock className="w-4 h-4" />
                    Mark as Unhandled
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4" />
                    Mark as Handled
                  </>
                )}
              </button>
              <button
                onClick={() => deleteContact(selectedContact._id)}
                className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2"
              >
                <Trash2 className="w-4 h-4" />
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
